import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Sheet } from '@paypalcorp/pp-react-sheet';
import { useTheme } from '@paypalcorp/pp-react-theme';
import '../__generated__/styles/action-menu.css';

var styles = {"ppvx_action-menu__container":"ppvx_action-menu__container___1-3-8","ppvx_action-menu":"ppvx_action-menu___1-3-8","ppvx_menu-list--multiline":"ppvx_menu-list--multiline___1-3-8","ppvx_menu-list__item--thumbnail":"ppvx_menu-list__item--thumbnail___1-3-8","ppvx_menu-list__item":"ppvx_menu-list__item___1-3-8","keyboard-active":"keyboard-active___1-3-8","ppvx_action-menu__item":"ppvx_action-menu__item___1-3-8","ppvx_menu-list__item--hilite":"ppvx_menu-list__item--hilite___1-3-8","ppvx_action-menu__item--text":"ppvx_action-menu__item--text___1-3-8","ppvx_action-menu__item--thumbnail":"ppvx_action-menu__item--thumbnail___1-3-8","ppvx_action-menu__item--icon":"ppvx_action-menu__item--icon___1-3-8","ppvx--v2":"ppvx--v2___1-3-8","ppvx--v1":"ppvx--v1___1-3-8"};

var getRefCurrent = function (ref) {
  return (
    /* istanbul ignore next */
    ref && ref.current ? ref.current : null
  );
};

var generateId = function () {
  return "actionMenu_".concat(Math.random().toFixed(8).toString().replace('0.', ''));
};

var SMALL_BREAKPOINT = 752; // key codes

var ESC = 27;
var TAB = 9;
var UP_ARROW = 38;
var DOWN_ARROW = 40;
var ENTER = 13;

var ActionMenu = function (_ref) {
  var className = _ref.className,
      containerClassName = _ref.containerClassName,
      itemClassName = _ref.itemClassName,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? generateId() : _ref$id,
      _ref$initialItemFocus = _ref.initialItemFocus,
      initialItemFocus = _ref$initialItemFocus === void 0 ? true : _ref$initialItemFocus,
      isOpen = _ref.isOpen,
      options = _ref.options,
      onClose = _ref.onClose,
      onSelection = _ref.onSelection,
      theme = _ref.theme,
      rest = _objectWithoutProperties(_ref, ["className", "containerClassName", "itemClassName", "id", "initialItemFocus", "isOpen", "options", "onClose", "onSelection", "theme"]);

  // If theme is not directly provided, get from themeContext
  var _theme = useTheme('ActionMenu', theme);

  var isThemeV2 = _theme && _theme === 'v2';
  var containerRef = useRef();
  var menuRef = useRef();
  var validateOptions = options && options.length;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      selectedIndex = _useState2[0],
      setSelectedIndex = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      keyboardActive = _useState4[0],
      setKeyboardActive = _useState4[1];
  /* istanbul ignore next */


  var isMobile = typeof window !== 'undefined' ? window && window.innerWidth < SMALL_BREAKPOINT : false;

  var setFocus = function (offset) {
    var current = getRefCurrent(menuRef);
    /* istanbul ignore next */

    if (current) {
      var _listItems = current.querySelectorAll('[role="menuitem"]');

      if (_listItems.length) {
        var len = _listItems.length;
        var nextIndex = selectedIndex + offset;
        var curIndex = nextIndex >= 0 && nextIndex <= len - 1 ? nextIndex : 0;

        _listItems[curIndex].focus();

        setSelectedIndex(curIndex);
      }
    }
  };

  var handleClickOutside = function (evt) {
    var wrapperCurrent = getRefCurrent(containerRef);

    if (wrapperCurrent && !wrapperCurrent.contains(evt.target)) {
      onClose(evt);
    }
  };

  var handleClick = function (index) {
    onSelection(index);
  };

  var handleKey = function (evt) {
    /* istanbul ignore next */
    var keyCode = evt.key || evt.keyCode;

    switch (keyCode) {
      case 'ArrowUp':
      case 'Up':
      case UP_ARROW:
        evt.preventDefault();
        setKeyboardActive(true);
        setFocus(-1);
        break;

      case 'ArrowDown':
      case 'Down':
      case DOWN_ARROW:
        evt.preventDefault();
        setKeyboardActive(true);
        setFocus(1);
        break;

      case 'Enter':
      case 'ENTER':
      case ENTER:
        evt.preventDefault();
        onSelection(selectedIndex);
        break;

      case 'Tab':
      case TAB:
      case 'Escape':
      case 'Esc':
      case ESC:
        onClose(evt);
        break;
    }
  };

  var actionMenuClasses = clsx(styles['ppvx_action-menu'], _defineProperty({}, styles['ppvx--v2'], isThemeV2), className, keyboardActive && styles['keyboard-active']);
  var itemClasses = clsx(styles['ppvx_action-menu__item'], itemClassName);
  var optionList = validateOptions ? options.map(function (_ref2, idx) {
    var itemLeft = _ref2.itemLeft,
        title = _ref2.title;
    return /*#__PURE__*/React.createElement("li", {
      className: itemClasses,
      role: "menuitem",
      onClick: function onClick() {
        return handleClick(idx);
      },
      key: "amenu_".concat(idx),
      tabIndex: -1,
      onKeyDown: function onKeyDown(evt) {
        return handleKey(evt);
      }
    }, itemLeft && /*#__PURE__*/React.createElement("span", {
      className: styles['ppvx_action-menu__item--thumbnail']
    }, itemLeft), /*#__PURE__*/React.createElement("p", {
      className: styles['ppvx_action-menu__item--text']
    }, title));
  }) : null;
  useEffect(function () {
    /* istanbul ignore next */
    if (isOpen) {
      if (!isMobile) {
        setFocus(0);
        setKeyboardActive(initialItemFocus);
      }

      document.addEventListener('click', handleClickOutside);
      window.addEventListener('resize', onClose);
    } else {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', onClose);
    }

    return function () {
      setSelectedIndex(0);
      setKeyboardActive(false);
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', onClose);
    };
  }, [isOpen]);
  var listItems = /*#__PURE__*/React.createElement("ul", _extends({
    className: actionMenuClasses,
    id: id,
    role: "menu",
    "aria-hidden": !isOpen,
    ref: menuRef
  }, rest), optionList);
  var containerClasses = clsx(styles['ppvx_action-menu__container'], _defineProperty({}, styles['ppvx--v2'], isThemeV2), containerClassName);
  var container = isMobile ? /*#__PURE__*/React.createElement(Sheet, {
    title: "action menu",
    isOpen: isOpen,
    onClose: onClose,
    theme: _theme,
    hideTitle: true,
    noCloseButton: true,
    noPadding: true
  }, listItems) : /*#__PURE__*/React.createElement("nav", {
    className: containerClasses,
    ref: containerRef
  }, listItems);
  return isOpen && container;
};

export { ActionMenu };
